import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import socket, { joinThread, leaveThread } from "../../shared/_helpers/socket";
import { useAuth } from "../../context/AuthContext.jsx";
import { readCurrentUser } from "../../shared/_helpers/currentUser";
import { CHOOSE_PLAN_PATH } from "../../shared/_helpers/chatGate";
import { isPlanRequired } from "../onboarding/onboardingNav";
import {
  getMyThread,
  getThreadMessages,
  markThreadRead,
  sendThreadMessage,
  uploadThreadAttachments,
} from "../../shared/_services/thread.services";
import {
  markedRead,
  messageReceived,
  messageSendFailed,
  messageSendRetried,
  messagesLoaded,
  selectAdvisors,
  selectMessagesOldestFirst,
  selectThread,
  selectThreadStatus,
  selectUnread,
  threadFailed,
  threadLoaded,
  threadLoading,
} from "../../redux/threadSlice";
import ThreadHeader from "./ThreadHeader";
import MessageList from "./MessageList";
import Composer from "./Composer";
import RosterCard from "./RosterCard";

// The client's thread (spec CAP-7; parity manifest items 1,2,5-8,10 + the
// web-only side column). Sends over HTTP; the socket only delivers.
export default function ThreadPage() {
  const dispatch = useDispatch();
  const store = useStore();
  const { enqueueSnackbar } = useSnackbar();
  const thread = useSelector(selectThread);
  const advisors = useSelector(selectAdvisors);
  const messages = useSelector(selectMessagesOldestFirst);
  const status = useSelector(selectThreadStatus);
  const nextBefore = useSelector((s) => s.thread.nextBefore);
  const me = readCurrentUser()?.user;
  // Retry drafts (fix round 1, finding 9): keyed by tempId, held here rather
  // than on the Redux message object. `files` are `File` instances, which
  // RTK's default `serializableCheck` flags as non-serializable state — a
  // component-level ref is a plain JS cache, invisible to Redux, and is the
  // right place for data that only this component's retry handler needs.
  //
  // This ref does not survive a remount (navigate away from /thread and
  // back), while a failed message bubble does — threadSlice.messagesLoaded
  // deliberately carries pending/failed messages across a re-fetch. Fix
  // round 3: when a failed message had attachments and this ref has no
  // entry for it (the remount case), retry() does nothing destructive — see
  // retry() below for why round 2's "move the text into the composer and
  // clear the bubble" approach was itself a regression, and was removed.
  const draftsRef = useRef(new Map());
  // Pooled inbox SP5 (R218, Review Focus 2): a client without chat access is
  // refused — 402 plan_required over HTTP, `plan_required` from the socket's
  // join. That is navigation to the server's routeFor (the plan page), never
  // an error message. Held in a ref so the effects below keep their deps.
  // A 402 body carries the entitlement the client no longer has; applying it
  // first keeps /plan from showing change-plan mode if the socket missed the
  // change.
  const navigate = useNavigate();
  const { updateEntitlement } = useAuth();
  const toPlan = useRef(null);
  toPlan.current = (routeFor, entitlement) => {
    updateEntitlement(entitlement);
    navigate(routeFor?.web || CHOOSE_PLAN_PATH, { replace: true });
  };
  const joinRoom = useCallback(async (threadId) => {
    const ack = await joinThread(threadId);
    if (ack?.code === "plan_required") toPlan.current(null);
  }, []);

  // Owner decision A (SP2-W14): a client's thread is marked read only while
  // this tab is actually the one the client is looking at — a background or
  // hidden tab must not clear the unread flag just because ThreadPage happens
  // to be mounted there. Resolves to null (no request) when the tab is
  // hidden; the caller decides how to handle that.
  const markReadIfVisible = useCallback(
    (threadId) => {
      if (document.visibilityState !== "visible") return null;
      return markThreadRead(threadId).then((r) => {
        dispatch(markedRead(r.data.lastReadAt));
        return r;
      });
    },
    [dispatch],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      dispatch(threadLoading());
      try {
        const res = await getMyThread();
        if (cancelled) return;
        dispatch(threadLoaded(res.data));
        const page = await getThreadMessages(res.data.thread._id, {
          limit: 50,
        });
        if (cancelled) return;
        dispatch(
          messagesLoaded({
            messages: page.data.messages,
            nextBefore: page.data.nextBefore,
          }),
        );
        if (!cancelled) await markReadIfVisible(res.data.thread._id);
      } catch (err) {
        if (!cancelled && isPlanRequired(err)) {
          toPlan.current(
            err.response.data.routeFor,
            err.response.data.entitlement,
          );
          return;
        }
        if (!cancelled) {
          dispatch(threadFailed());
          enqueueSnackbar("Couldn't load your thread.", { variant: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, enqueueSnackbar, markReadIfVisible]);

  useEffect(() => {
    // AuthContext opens the socket once the session verifies (SEC-085).
    if (!thread?._id) return undefined;
    const onMessage = (m) => {
      if (String(m.threadId) !== String(thread._id)) return;
      dispatch(messageReceived(m));
      if (m.senderType === "advisor")
        markReadIfVisible(thread._id)?.catch(() => {});
    };
    const onReconnect = async () => {
      await joinRoom(thread._id);
      try {
        const page = await getThreadMessages(thread._id, { limit: 50 });
        dispatch(
          messagesLoaded({
            messages: page.data.messages,
            nextBefore: page.data.nextBefore,
          }),
        );
      } catch (_) {
        /* the next open refetches */
      }
    };
    joinRoom(thread._id);
    socket.on("message received", onMessage);
    socket.on("connect", onReconnect);
    return () => {
      socket.off("message received", onMessage);
      socket.off("connect", onReconnect);
      leaveThread(thread._id);
    };
  }, [thread?._id, dispatch, markReadIfVisible, joinRoom]);

  // Owner decision A: when this tab becomes visible again and there are
  // unread messages, mark read then. Reads selectUnread from the store at
  // event time (not a captured value) so a listener registered once doesn't
  // act on a stale unread flag.
  useEffect(() => {
    if (!thread?._id) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!selectUnread(store.getState())) return;
      markReadIfVisible(thread._id)?.catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [thread?._id, store, markReadIfVisible]);

  const send = useCallback(
    async ({ text, files }, tempId = `tmp-${Date.now()}-${Math.random()}`) => {
      if (!thread?._id) return;
      draftsRef.current.set(tempId, { text, files });
      dispatch(
        messageReceived({
          tempId,
          _id: tempId,
          threadId: thread._id,
          senderType: "client",
          sender: {
            _id: me?._id,
            firstName: me?.firstName,
            lastName: me?.lastName,
          },
          kind: files.length ? "file" : "text",
          handoffFrom: null,
          content: {
            text,
            attachments: files.map((f) => ({
              documentId: f.name,
              filename: f.name,
              size: f.size,
              url: null,
            })),
          },
          createdAt: new Date().toISOString(),
          pending: true,
        }),
      );
      try {
        const attachments = files.length
          ? await uploadThreadAttachments(files)
          : [];
        const res = await sendThreadMessage(thread._id, {
          text,
          attachments: attachments.map((a) => ({ documentId: a.documentId })),
        });
        dispatch(messageReceived({ ...res.data.message, tempId }));
        draftsRef.current.delete(tempId);
      } catch (err) {
        dispatch(messageSendFailed({ tempId, text }));
        if (isPlanRequired(err))
          toPlan.current(
            err.response.data.routeFor,
            err.response.data.entitlement,
          );
        // Left in draftsRef on failure — retry() below needs it.
      }
    },
    [thread?._id, dispatch, me?._id, me?.firstName, me?.lastName],
  );

  const retry = useCallback(
    (failed) => {
      // Read the draft BEFORE dispatching messageSendRetried (fix round 1,
      // finding 8): it works today only because `failed` is a captured
      // reference and the reducer filters the array rather than mutating the
      // message object in place. A future reducer that cleared fields in
      // place instead would silently lose the user's text — the exact
      // carry-forward hazard this task was given. Reading into a local first
      // removes the ordering dependency on the reducer's implementation.
      const draft = draftsRef.current.get(failed.tempId);
      const hadAttachments = (failed.content?.attachments?.length || 0) > 0;

      if (!draft && hadAttachments) {
        // Fix round 3: round 2 responded to the same unrecoverable-
        // attachment case by clearing the failed bubble and pushing its text
        // into the composer via a ref/imperative handle — which then
        // silently clobbered whatever the client was mid-typing for their
        // *next* message, with no merge and no warning. That failed bubble
        // is real, user-visible state; overwriting it, or overwriting the
        // composer on the client's behalf, is the same category of defect
        // this whole fix chain started from, just pointed a different way.
        // So: do nothing destructive. Leave the failed bubble exactly where
        // it is (its attachments and text stay visible as the record that
        // something needs re-attaching), leave the composer untouched, and
        // only surface the explanation. No `send()`, no
        // `messageSendRetried` dispatch, no composer write.
        enqueueSnackbar(
          "This message had an attachment that couldn't be resent automatically. Please re-attach your file(s) and send it again.",
          { variant: "info" },
        );
        return;
      }

      const toSend = draft || { text: failed.content?.text ?? "", files: [] };
      draftsRef.current.delete(failed.tempId);
      dispatch(messageSendRetried(failed.tempId));
      send(toSend);
    },
    [dispatch, send, enqueueSnackbar],
  );

  const loadOlder = useCallback(async () => {
    if (!thread?._id || !nextBefore) return;
    const page = await getThreadMessages(thread._id, {
      before: nextBefore,
      limit: 50,
    });
    dispatch(
      messagesLoaded({
        messages: page.data.messages,
        nextBefore: page.data.nextBefore,
        prepend: true,
      }),
    );
  }, [thread?._id, nextBefore, dispatch]);

  return (
    <div
      data-slot="thread-page"
      className="tw-mx-auto tw-flex tw-h-screen tw-max-w-4xl tw-flex-col tw-px-4 tw-pt-8 tw-pb-4 max-rail:tw-h-auto max-rail:tw-pt-6"
    >
      <section
        data-slot="thread-main"
        className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
      >
        <ThreadHeader />
        <RosterCard advisors={advisors} />
        {status === "loading" && (
          <p className="tw-text-text-muted">Loading…</p>
        )}
        <MessageList
          messages={messages}
          onLoadOlder={loadOlder}
          hasOlder={!!nextBefore}
          onRetry={retry}
        />
        <Composer onSend={send} disabled={status !== "ready"} />
      </section>
    </div>
  );
}
